// Ported from @Corim/architecture-backend (Store + Dispatcher) so the app no
// longer depends on the private GitHub Packages registry.
import _ from 'lodash'
import { from, of, queueScheduler, Observable } from 'rxjs'
import {
  filter, map, scan, distinctUntilChanged, mergeMap, groupBy,
  observeOn, startWith, shareReplay, share, delay,
} from 'rxjs/operators'

const isProduction = process.env.NODE_ENV === 'production'

// ---------------------------------------------------------------------------
// State store: a scan over update functions, replayed to late subscribers.
// ---------------------------------------------------------------------------

const setObject = (object, keypath, value) => {
  const tempObject = _.cloneDeep(object)
  const oldValue = _.get(tempObject, keypath)
  const oldValueFixed = oldValue ? oldValue : {}
  return _.set(tempObject, keypath, { ...oldValueFixed, ...value })
}

let dispatchState

const stateUpdate$ = Observable.create(observer => {
  dispatchState = update => observer.next(update)
})
  .pipe(
    // Forces every value to get rendered before the next value is calculated
    observeOn(queueScheduler)
  )

const Store = stateUpdate$
  .pipe(
    // If the updater returns null, keep the previous state
    scan((acc, next) => next(acc) ?? acc, {}),
    startWith({}),
    shareReplay(1)
  )

// Keep the store hot so `dispatchState` is assigned and state accumulates
Store.subscribe(() => {})

const getState = (keypath = null) => {
  if (_.isNil(keypath)) {
    return Store
  }
  return Store
    .pipe(
      mergeMap(e => {
        const unstarredId = keypath.replace('.*', '')
        const value = _.get(e, unstarredId)
        if (keypath.substring(keypath.length - 2, keypath.length) === '.*') {
          return from(Object.keys(value).map(key => ({ id: key, value: { id: key, value: value[key] } })))
        }
        return of({ id: null, value })
      }),
      groupBy(e => e.id),
      mergeMap(group => group
        .pipe(
          map(object => object.value),
          distinctUntilChanged((a, b) => _.isEqual(a, b))
        )
      ),
      filter(e => !_.isUndefined(e))
    )
}

const nextState = (key, value) =>
  dispatchState(acc => {
    if (!_.isEqual(_.get(acc, key), value)) {
      // Clone for dev, otherwise set directly for speed
      return !isProduction ? _.set(_.cloneDeep(acc), key, value) : _.set(acc, key, value)
    }
    // Return null if no changes
    return null
  })

const mergeState = (key, value) =>
  dispatchState(acc => setObject(acc, key, value))

// ---------------------------------------------------------------------------
// Actions: a shared event bus addressed by dot-separated ids ('Response.contact').
// A trailing '.*' subscribes to every action under that prefix.
// ---------------------------------------------------------------------------

let dispatchAction

const action$ = Observable.create(observer => {
  dispatchAction = action => observer.next(action)
})
  .pipe(
    observeOn(queueScheduler),
    // Delay allows state to update in between dispatches, otherwise a rapid
    // series of dispatches can read stale state and override values
    delay(1),
    share()
  )

// Keep the bus hot so `dispatchAction` is assigned before the first action
action$.subscribe(() => {})

const getAction = (listener) => {
  const id = listener ? listener : ''
  const starred = id.substring(id.length - 2, id.length) === '.*'
  return action$
    .pipe(
      filter(update => {
        if (starred) {
          const unstarredId = id.replace('.*', '')
          const listenerSubstring = unstarredId.split('.')
          const updateSubstring = update.id.split('.')
          return listenerSubstring.reduce((acc, next, index) => acc && updateSubstring[index] === next, true)
        }
        return id === update.id
      }),
      map(update => {
        if (!starred) {
          return update.value
        }
        const unstarredId = id.replace('.*', '')
        let updatedId = update.id.replace(unstarredId, '')
        if (updatedId.substring(0, 1) === '.') {
          updatedId = updatedId.substring(1, updatedId.length)
        }
        return { id: updatedId, value: update.value }
      })
    )
}

const nextAction = (id, value) =>
  dispatchAction({ id, value })

export default {
  getState,
  nextState,
  mergeState,
  getAction,
  nextAction,
}
